import {
  resolveShowcaseSubPageSlugs,
  type ShowcaseSubPage,
} from '~/components/v2/components/showcase/showcaseSubPageRoutes';

/**
 * One rendered destination in the public navigation chrome — the synthetic Home entry, an authored
 * sub-page, or an authored external link. Active state is resolved here rather than in the
 * components so the sticky bar and the mobile menu can never disagree about where the viewer is.
 */
export type PublicShowcaseNavEntry = {
  /**
   * An authored external link's destination URL — present ONLY when `isExternal`. Internal entries
   * carry no href here: their links are built per-surface by the navigation contract's
   * `getPageNavigationHref` (the live page and the Preview iframe have different route bases, and
   * the Edit tab has none).
   */
  externalHref?: string;
  id: string;
  isActive: boolean;
  isExternal: boolean;
  label: string;
  /**
   * Which editor page this entry addresses — read ONLY by the inline edit canvas, where the nav
   * drives the editor's active-page state instead of routing. `null` is the root ("Home") page, a
   * collection sub-page carries its `collectionId`, and an external link carries `null` (it never
   * drives editor state — an external entry always routes to its URL). `null` is a designed value
   * here, not "unknown": every entry has exactly one page identity, which is why this is
   * `string | null` rather than optional. On the real public page and the Preview iframe this field
   * is ignored entirely; active state comes from the URL. See `usePublicShowcaseNavEntries`.
   */
  pageId: string | null;
  /**
   * The sub-page's canonical slug, used to build the `?page={slug}` board route when a board is
   * opened from this entry's board dropdown (so the board's breadcrumbs read `Home / {Page} /
   * {Board}`). `null` is the root ("Home") page — its boards route to the flat `/a/{shortId}/b/...`
   * with no page trail — and an external link (which never lists boards). Like `pageId`, `null` is
   * a designed value: every entry has one page identity, and only a collection sub-page carries a
   * slug, so this is `string | null` rather than optional.
   */
  slug: string | null;
};

/**
 * Home is synthetic: `presentationPages.subPages` lists only the authored sub-pages, and the link's
 * root page is always the first destination. The prototype had an authorable `homePageLabel`; no
 * backend field was ever built for it, so the label is fixed.
 */
export const PUBLIC_SHOWCASE_NAV_HOME_ID = 'home';
export const PUBLIC_SHOWCASE_NAV_HOME_LABEL = 'Home';

/**
 * Same protocol guard the authored links block applies (`PublicShowcaseLinksBlock`): an authored
 * destination only becomes an anchor when it is http(s), so a `javascript:` or malformed URL can
 * never render as a clickable nav entry. Together with the empty-URL case, a link sub-page that
 * goes nowhere is left out of the bar entirely rather than rendered inert — the prototype's
 * behavior, and better than a nav item that does nothing when clicked.
 */
const getSafeNavHref = (url: string | undefined): string | undefined => {
  const trimmedUrl = url?.trim();

  if (!trimmedUrl) {
    return undefined;
  }

  try {
    const { protocol } = new URL(trimmedUrl);

    return protocol === 'http:' || protocol === 'https:' ? trimmedUrl : undefined;
  } catch {
    return undefined;
  }
};

/**
 * A page hidden from the navigation (`draft: true` on its child collection) is not a destination on
 * any surface. The live page never has to ask — the unauthenticated share-link read omits hidden
 * pages server-side — but the Preview iframe and the Edit-tab canvas render the EDITOR'S DRAFT,
 * where nothing has omitted them, so the filter has to happen here or those two surfaces would
 * offer the viewer pages the viewer cannot reach.
 *
 * `link` entries pass through untouched: an authored external link has no child collection and so no
 * visibility of its own.
 */
const getVisibleSubPages = (subPages: readonly ShowcaseSubPage[]): readonly ShowcaseSubPage[] =>
  subPages.filter((subPage) => subPage.type !== 'collection' || subPage.draft !== true);

export type BuildPublicShowcaseNavEntriesParams = {
  /** Canonical slug of the sub-page the viewer is on, or null on the link's root page. */
  activeSlug: string | null;
  subPages: readonly ShowcaseSubPage[];
};

/**
 * Builds the ordered nav destinations for a share link: Home first, then the VISIBLE `subPages` in
 * authored order.
 */
export const buildPublicShowcaseNavEntries = ({
  activeSlug,
  subPages,
}: BuildPublicShowcaseNavEntriesParams): PublicShowcaseNavEntry[] => {
  /*
   * Hidden pages are dropped BEFORE slugs are resolved, and the order is load-bearing:
   * `resolveShowcaseSubPageSlugs` dedupes repeated names by list position, so a hidden page that
   * participated would push the `-2` suffix onto a VISIBLE page. Live, the server never sends the
   * hidden page and slugs are resolved among visible pages only — filtering first is what makes the
   * Preview iframe's URLs identical to production's rather than merely similar.
   */
  const visibleSubPages = getVisibleSubPages(subPages);
  // Slugs come from the shared resolver (dedupe suffixes included) so a rendered href always
  // resolves back through `findShowcaseSubPageBySlug` to the page it names.
  const slugsBySubPage = new Map(
    resolveShowcaseSubPageSlugs(visibleSubPages).map(({ slug, subPage }) => [subPage, slug]),
  );

  const entries: PublicShowcaseNavEntry[] = [
    {
      id: PUBLIC_SHOWCASE_NAV_HOME_ID,
      isActive: !activeSlug,
      isExternal: false,
      label: PUBLIC_SHOWCASE_NAV_HOME_LABEL,
      // The root page — the editor represents it as `activePageId === null`.
      pageId: null,
      // Home's boards route to the flat board path with no page trail.
      slug: null,
    },
  ];

  visibleSubPages.forEach((subPage, index) => {
    if (subPage.type === 'link') {
      const href = getSafeNavHref(subPage.url);

      if (!href) {
        return;
      }

      entries.push({
        externalHref: href,
        // Link sub-pages carry no id on the wire and two of them can share a label AND a URL, so
        // position is the only stable key — position among the VISIBLE entries, which is the same
        // position the live page's own (already-filtered) list gives it.
        id: `link-${index}`,
        isActive: false,
        isExternal: true,
        label: subPage.label,
        // External links have no editor page — they open their URL in a new tab even inside the
        // canvas, so the canvas active-state match never uses this.
        pageId: null,
        // External links never list boards.
        slug: null,
      });

      return;
    }

    const slug = slugsBySubPage.get(subPage);

    if (!slug) {
      return;
    }

    entries.push({
      id: `page-${slug}`,
      isActive: activeSlug === slug,
      isExternal: false,
      label: subPage.name,
      // The editor addresses a collection sub-page by its own `collectionId` (see
      // ShowcaseEditorNavigationProvider's `setActivePage`).
      pageId: subPage.collectionId,
      // The same slug the sub-page route uses, so a board opened from this entry's dropdown carries
      // the matching `?page={slug}` trail.
      slug,
    });
  });

  return entries;
};
